import React, { useState, useEffect } from 'react';

// /overlay?show=tip: Togepi waddles in, spots a coin that drops from the sky, picks it up, holds it high and jumps for
// joy while money rains down, then waddles off with it. Drawn here in SVG (none of the site's images) and played once
// each time the page loads, so in OBS it replays whenever the source is shown (tick "Shutdown source when not visible").
//   /overlay?show=tip&demo=1     plays it on repeat, for lining it up
//   /overlay?show=tip&at=3500    stops it at that moment (ms), to see one frame

const DURATION = 7000;  // the whole animation, ms. The CSS below is timed in percentages of this
const DEMO_GAP = 1500;  // pause between plays on repeat
const GROUND = 470;     // where Togepi's feet are, in the 900 x 500 stage
const HOME = 450;       // where he stops, across the stage
const INK = '#3a2f2a';
const SKIN = '#fff3c4';

const CSS = `
  .tip-walk, .tip-rock, .tip-jump, .tip-bend { transform-origin: 0px 0px; }
  .tip-walk { animation: tipWalkIn 1.2s ease-out both, tipWalkOut 1s ease-in 6s forwards; }
  @keyframes tipWalkIn { from { transform: translateX(-620px); } to { transform: translateX(0); } }
  @keyframes tipWalkOut { from { transform: translateX(0); } to { transform: translateX(640px); } }
  .tip-rock { animation: tipRock 0.4s ease-in-out 0s 3, tipRock 0.4s ease-in-out 6s 3; }
  @keyframes tipRock {
    0%, 50%, 100% { transform: rotate(0) translateY(0); }
    25% { transform: rotate(-7deg) translateY(-6px); }
    75% { transform: rotate(7deg) translateY(-6px); }
  }
  .tip-jump { animation: tipJump 7s linear both; }
  @keyframes tipJump {
    0%, 45%, 66%, 100% { transform: translateY(0) scale(1, 1); }
    47%, 55%, 63% { transform: translateY(0) scale(1.1, 0.9); animation-timing-function: ease-out; }
    51%, 59% { transform: translateY(-70px) scale(0.95, 1.08); animation-timing-function: ease-in; }
  }
  .tip-shadow { transform-box: fill-box; transform-origin: center; animation: tipShadow 7s linear both; }
  @keyframes tipShadow {
    0%, 47%, 55%, 63%, 100% { transform: scale(1); opacity: 0.35; }
    51%, 59% { transform: scale(0.6); opacity: 0.15; }
  }
  /* Bends forward to pick the coin up, then straightens */
  .tip-bend { animation: tipBend 7s ease-in-out both; }
  @keyframes tipBend { 0%, 22% { transform: rotate(0); } 29%, 36% { transform: rotate(35deg); } 44%, 100% { transform: rotate(0); } }
  .tip-arm-l { transform-origin: -62px -118px; animation: tipArmL 7s ease-in-out both; }
  .tip-arm-r { transform-origin: 62px -118px; animation: tipArmR 7s ease-in-out both; }
  @keyframes tipArmL { 0%, 36% { transform: rotate(0); } 44%, 100% { transform: rotate(75deg); } }
  @keyframes tipArmR { 0%, 22% { transform: rotate(0); } 29%, 36% { transform: rotate(50deg); } 44%, 100% { transform: rotate(-75deg); } }
  .tip-eyes { animation: tipEyes 7s step-end both; }
  .tip-happy { animation: tipHappy 7s step-end both; }
  @keyframes tipEyes { 0% { opacity: 1; } 44% { opacity: 0; } }
  @keyframes tipHappy { 0% { opacity: 0; } 44% { opacity: 1; } }
  .tip-bang { transform-box: fill-box; transform-origin: center; animation: tipBang 7s ease-out both; }
  @keyframes tipBang {
    0%, 18% { transform: scale(0.3); opacity: 0; }
    20% { transform: scale(1.25); opacity: 1; }
    22%, 26% { transform: scale(1); opacity: 1; }
    28%, 100% { transform: scale(1); opacity: 0; }
  }
  /* The coin: falls, bounces, waits, and is gone the moment he picks it up */
  .tip-drop { animation: tipDrop 7s linear both; }
  @keyframes tipDrop {
    0%, 9% { transform: translateY(-520px); opacity: 1; animation-timing-function: ease-in; }
    19% { transform: translateY(0); animation-timing-function: ease-out; }
    22% { transform: translateY(-36px); animation-timing-function: ease-in; }
    25% { transform: translateY(0); animation-timing-function: ease-out; }
    27% { transform: translateY(-8px); animation-timing-function: ease-in; }
    28%, 35.9% { transform: translateY(0); opacity: 1; }
    36%, 100% { transform: translateY(0); opacity: 0; }
  }
  .tip-spin { transform-box: fill-box; transform-origin: center; animation: tipSpin 0.5s linear 0.6s 3; }
  @keyframes tipSpin { 0%, 100% { transform: scaleX(1); } 50% { transform: scaleX(0.12); } }
  /* The same coin in his hands: from where he picked it up to above his head */
  .tip-held { animation: tipHeld 7s ease-out both; }
  @keyframes tipHeld {
    0%, 35.9% { transform: translate(80px, -92px); opacity: 0; }
    36% { transform: translate(80px, -92px); opacity: 1; }
    44%, 100% { transform: translate(0, -268px); opacity: 1; }
  }
  .tip-shine { transform-box: fill-box; transform-origin: center; animation: tipShine 0.6s ease-in-out 3.1s infinite; }
  @keyframes tipShine { 0%, 100% { transform: scale(1); } 50% { transform: scale(1.12); } }
  .tip-sparkle { transform-box: fill-box; transform-origin: center; animation: tipSparkle 0.9s ease-in-out both infinite; }
  @keyframes tipSparkle {
    0%, 100% { transform: scale(0) rotate(0); opacity: 0; }
    50% { transform: scale(1) rotate(90deg); opacity: 1; }
  }
  .tip-text { transform-box: fill-box; transform-origin: center; animation: tipText 7s ease-out both; }
  @keyframes tipText {
    0%, 43% { transform: scale(0); opacity: 0; }
    48% { transform: scale(1.2); opacity: 1; }
    52%, 88% { transform: scale(1); opacity: 1; }
    94%, 100% { transform: scale(0.8); opacity: 0; }
  }
  .tip-rain { transform-box: fill-box; transform-origin: center; animation-name: tipRain; animation-timing-function: ease-in; animation-fill-mode: both; }
  @keyframes tipRain {
    from { transform: translateY(-100px) rotate(0); }
    to { transform: translateY(640px) rotate(var(--spin)); }
  }
`;

// What rains down while he celebrates: [across the stage, start (s), how long it falls (s), spin, coin or note]
const RAIN = [
  [70, 3.0, 1.9, 300, 'coin'], [180, 3.4, 1.6, -220, 'note'], [290, 3.1, 2.0, 260, 'coin'], [380, 3.9, 1.7, -320, 'coin'],
  [520, 3.2, 1.8, 200, 'note'], [610, 3.6, 1.5, -280, 'coin'], [720, 3.0, 2.1, 340, 'coin'], [830, 3.5, 1.7, -240, 'note'],
  [130, 4.2, 1.6, 260, 'coin'], [240, 4.6, 1.8, -200, 'note'], [460, 4.4, 1.5, 300, 'coin'], [570, 4.9, 1.7, -260, 'coin'],
  [680, 4.3, 1.9, 220, 'note'], [790, 4.8, 1.6, -300, 'coin'], [340, 5.1, 1.4, 240, 'coin'], [860, 4.1, 1.8, 280, 'coin'],
];

const Coin = ({ r = 20 }) => (
  <g>
    <circle r={r} fill="url(#tip-gold)" stroke="#a8740a" strokeWidth="3" />
    <circle r={r * 0.7} fill="none" stroke="#fff3b0" strokeWidth="2" opacity="0.8" />
    <text y={r * 0.38} textAnchor="middle" fontSize={r * 1.05} fontWeight="900" fill="#a8740a" fontFamily="Arial, sans-serif">£</text>
  </g>
);

const Note = () => (
  <g>
    <rect x="-32" y="-16" width="64" height="32" rx="4" fill="#7fd48a" stroke="#2e7d32" strokeWidth="2.5" />
    <rect x="-26" y="-11" width="52" height="22" rx="3" fill="none" stroke="#2e7d32" strokeWidth="1.5" opacity="0.6" />
    <circle r="8" fill="#b9ecc0" stroke="#2e7d32" strokeWidth="1.5" />
    <text y="4.5" textAnchor="middle" fontSize="12" fontWeight="900" fill="#2e7d32" fontFamily="Arial, sans-serif">£</text>
  </g>
);

const Sparkle = ({ x, y, size = 14, delay }) => (
  <g transform={`translate(${x} ${y})`}>
    <path className="tip-sparkle" style={{ animationDelay: `${delay}s` }} fill="#fff7c2"
      d={`M0,${-size} Q${size * 0.18},${-size * 0.18} ${size},0 Q${size * 0.18},${size * 0.18} 0,${size} Q${-size * 0.18},${size * 0.18} ${-size},0 Q${-size * 0.18},${-size * 0.18} 0,${-size} Z`} />
  </g>
);

// Togepi, with his feet at 0,0: the spiky crown and head, his eggshell with its red and blue patches, little feet and arms
function Togepi() {
  const shell = 'M-80,-96 L-64,-112 L-48,-94 L-32,-114 L-16,-94 L0,-114 L16,-94 L32,-114 L48,-94 L64,-112 L80,-96 C86,-44 52,0 0,0 C-52,0 -86,-44 -80,-96 Z';
  return (
    <g strokeLinejoin="round" strokeLinecap="round">
      <ellipse cx="-34" cy="-6" rx="24" ry="13" fill={SKIN} stroke={INK} strokeWidth="4" />
      <ellipse cx="34" cy="-6" rx="24" ry="13" fill={SKIN} stroke={INK} strokeWidth="4" />
      <path d="M-46,-170 L-60,-206 L-28,-190 L-16,-228 L0,-196 L16,-228 L28,-190 L60,-206 L46,-170 Z" fill={SKIN} stroke={INK} strokeWidth="4" />
      <ellipse cx="0" cy="-140" rx="66" ry="60" fill={SKIN} stroke={INK} strokeWidth="4" />
      <g className="tip-eyes">
        {[-1, 1].map((side) => (
          <g key={side}>
            <ellipse cx={side * 24} cy="-152" rx="8" ry="12" fill="#1d1d22" />
            <circle cx={side * 24 - 3} cy="-157" r="3.5" fill="#fff" />
          </g>
        ))}
      </g>
      <g className="tip-happy" fill="none" stroke="#1d1d22" strokeWidth="5">
        <path d="M-33,-148 Q-24,-163 -15,-148" />
        <path d="M15,-148 Q24,-163 33,-148" />
      </g>
      <ellipse cx="-44" cy="-131" rx="10" ry="6" fill="#ff8fa3" opacity="0.6" />
      <ellipse cx="44" cy="-131" rx="10" ry="6" fill="#ff8fa3" opacity="0.6" />
      <path d="M-12,-128 Q0,-110 12,-128 Z" fill="#8a2f36" stroke={INK} strokeWidth="3" />
      <path d={shell} fill="#ffffff" stroke={INK} strokeWidth="4" />
      <g clipPath="url(#tip-shell)">
        <path d="M-74,-74 L-34,-48 L-70,-22 Z" fill="#e5484d" />
        <path d="M28,-70 L70,-84 L60,-34 Z" fill="#3b82f6" />
        <path d="M2,-34 L36,-22 L8,-6 Z" fill="#e5484d" />
        <path d="M-30,-20 L-8,-10 L-32,0 Z" fill="#3b82f6" />
      </g>
      <path d={shell} fill="none" stroke={INK} strokeWidth="4" />
      <g className="tip-arm-l"><ellipse cx="-76" cy="-116" rx="15" ry="11" fill={SKIN} stroke={INK} strokeWidth="4" /></g>
      <g className="tip-arm-r"><ellipse cx="76" cy="-116" rx="15" ry="11" fill={SKIN} stroke={INK} strokeWidth="4" /></g>
    </g>
  );
}

export default function TipAlert({ loop, at }) {
  const [play, setPlay] = useState(0);
  useEffect(() => {
    if (!loop) return undefined;
    const timer = setInterval(() => setPlay((n) => n + 1), DURATION + DEMO_GAP);
    return () => clearInterval(timer);
  }, [loop]);

  // One frame: every animation stopped at the same moment
  useEffect(() => {
    if (at == null) return;
    document.getAnimations().forEach((animation) => { animation.pause(); animation.currentTime = at; });
  }, [at]);

  return (
    <svg key={play} viewBox="0 0 900 500" preserveAspectRatio="xMidYMax meet" role="img" aria-label="Togepi picks up a tip"
      style={{ position: 'fixed', inset: 0, width: '100%', height: '100%', overflow: 'hidden', filter: 'drop-shadow(0 6px 10px rgba(0,0,0,0.45))' }}>
      <style>{CSS}</style>
      <defs>
        <radialGradient id="tip-gold" cx="35%" cy="30%" r="75%">
          <stop offset="0" stopColor="#fff2a8" />
          <stop offset="0.45" stopColor="#f8c936" />
          <stop offset="1" stopColor="#d99a12" />
        </radialGradient>
        <linearGradient id="tip-brand" x1="0" y1="0" x2="1" y2="0">
          <stop offset="0" stopColor="#04aff7" />
          <stop offset="1" stopColor="#f42ae4" />
        </linearGradient>
        <clipPath id="tip-shell">
          <path d="M-80,-96 L-64,-112 L-48,-94 L-32,-114 L-16,-94 L0,-114 L16,-94 L32,-114 L48,-94 L64,-112 L80,-96 C86,-44 52,0 0,0 C-52,0 -86,-44 -80,-96 Z" />
        </clipPath>
      </defs>

      {RAIN.map(([x, start, fall, spin, kind], i) => (
        <g key={i} transform={`translate(${x} 0)`}>
          <g className="tip-rain" style={{ animationDelay: `${start}s`, animationDuration: `${fall}s`, '--spin': `${spin}deg` }}>
            {kind === 'coin' ? <Coin r={16} /> : <Note />}
          </g>
        </g>
      ))}

      <text className="tip-text" x="450" y="130" textAnchor="middle" fontSize="88" fontWeight="900" fontFamily="'Arial Black', Impact, sans-serif"
        fill="url(#tip-brand)" stroke="#ffffff" strokeWidth="10" paintOrder="stroke" letterSpacing="2">THANK YOU!</text>

      <g transform={`translate(${HOME + 118} ${GROUND - 22})`}>
        <g className="tip-drop"><g className="tip-spin"><Coin r={22} /></g></g>
      </g>

      <g transform={`translate(${HOME} ${GROUND})`}>
        <g className="tip-walk">
          <ellipse className="tip-shadow" cx="0" cy="4" rx="70" ry="10" fill="#000" />
          <g className="tip-rock">
            <g className="tip-jump">
              <g className="tip-bend">
                <Togepi />
                <g className="tip-held">
                  <g className="tip-shine"><Coin r={24} /></g>
                  <Sparkle x={-38} y={-18} delay={3.1} />
                  <Sparkle x={36} y={-30} size={10} delay={3.4} />
                  <Sparkle x={30} y={22} size={12} delay={3.7} />
                </g>
              </g>
              <text className="tip-bang" x="0" y="-250" textAnchor="middle" fontSize="64" fontWeight="900" fontFamily="'Arial Black', Impact, sans-serif"
                fill="#ffd23f" stroke={INK} strokeWidth="6" paintOrder="stroke">!</text>
            </g>
          </g>
        </g>
      </g>
    </svg>
  );
}
